import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowLeft, ChevronRight } from "lucide-react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_interpret-now/artifacts/8sijz41w_PassportTalk.png";

const asianLanguages = [
  {
    code: "zh",
    flagUrl: "https://flagcdn.com/w40/cn.png",
    flagUrl2x: "https://flagcdn.com/w80/cn.png",
    nativeLabel: "中文",
    englishLabel: "Chinese",
    color: "#dc2626",
    fullExperience: false
  },
  {
    code: "ja",
    flagUrl: "https://flagcdn.com/w40/jp.png",
    flagUrl2x: "https://flagcdn.com/w80/jp.png",
    nativeLabel: "日本語",
    englishLabel: "Japanese",
    color: "#be123c",
    fullExperience: false
  },
  {
    code: "ko",
    flagUrl: "https://flagcdn.com/w40/kr.png",
    flagUrl2x: "https://flagcdn.com/w80/kr.png",
    nativeLabel: "한국어",
    englishLabel: "Korean",
    color: "#1d4ed8",
    fullExperience: false
  },
  {
    code: "vi",
    flagUrl: "https://flagcdn.com/w40/vn.png",
    flagUrl2x: "https://flagcdn.com/w80/vn.png",
    nativeLabel: "Tiếng Việt",
    englishLabel: "Vietnamese",
    color: "#dc2626",
    fullExperience: false
  },
  {
    code: "th",
    flagUrl: "https://flagcdn.com/w40/th.png",
    flagUrl2x: "https://flagcdn.com/w80/th.png",
    nativeLabel: "ภาษาไทย",
    englishLabel: "Thai",
    color: "#0369a1",
    fullExperience: false
  }
];

const AsiaLanguageSelectionPage = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [selected, setSelected] = useState(null);

  const handleSelection = (lang) => {
    setSelected(lang.code);
    
    // Save to localStorage
    localStorage.setItem('passportTalk_primaryLanguage', lang.code);
    
    // Change i18n language
    i18n.changeLanguage(lang.code);
    
    // Check if user is authenticated
    const token = localStorage.getItem('token');
    
    // Route based on auth status
    setTimeout(() => {
      if (token) {
        // Authenticated users go to conversation
        navigate('/conversation');
      } else {
        // Unauthenticated users go to login (language preference is already saved)
        navigate('/login');
      }
    }, 300);
  };

  const handleBack = () => {
    // Check if user has token (is authenticated) - if so, go to language-select, otherwise go to landing
    const token = localStorage.getItem('token');
    if (token) {
      navigate('/language-select');
    } else {
      // Clear the primary language so landing page shows language selection again
      localStorage.removeItem('passportTalk_primaryLanguage');
      navigate('/');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white flex flex-col items-center justify-center p-6">
      <div className="max-w-md w-full">
        {/* Back Button */}
        <button
          onClick={handleBack}
          className="mb-6 flex items-center gap-2 text-slate-600 hover:text-slate-900 transition-colors"
          data-testid="back-to-languages"
        >
          <ArrowLeft className="w-5 h-5" />
          <span className="text-sm font-medium">{t('common.back', 'Back')}</span>
        </button>

        {/* Logo & Header */}
        <div className="text-center mb-8">
          <img 
            src={LOGO_URL} 
            alt="PassportTalk" 
            className="w-16 h-16 mx-auto mb-4 object-contain"
          />
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 font-outfit mb-2">
            {t('languageSelect.asiaTitle', 'Select Your Language')}
          </h1>
          <p className="text-slate-600 text-sm">
            {t('languageSelect.asiaSubtitle', 'Choose your preferred Asian language')}
          </p>
        </div>

        {/* Asian Language Options */}
        <div className="space-y-3">
          {asianLanguages.map((lang) => (
            <button
              key={lang.code}
              onClick={() => handleSelection(lang)}
              className={`w-full p-4 rounded-xl border-2 transition-all duration-200 ease-out text-left group
                ${selected === lang.code 
                  ? 'shadow-lg scale-[1.02]' 
                  : 'border-slate-200 bg-white hover:border-slate-300 hover:shadow-md hover:-translate-y-0.5'
                }`}
              style={{
                borderColor: selected === lang.code ? lang.color : undefined,
                backgroundColor: selected === lang.code ? `${lang.color}10` : undefined
              }}
              data-testid={`language-${lang.code}`}
            >
              <div className="flex items-center gap-4">
                {/* Flag Icon */}
                <div className="w-12 h-8 rounded overflow-hidden shadow-sm border border-slate-200 flex-shrink-0 transition-transform duration-200 ease-out group-hover:scale-[1.04]">
                  <img 
                    src={lang.flagUrl}
                    srcSet={`${lang.flagUrl2x} 2x`}
                    alt={`${lang.englishLabel} flag`}
                    className="w-full h-full object-cover"
                    loading="eager"
                  />
                </div>
                <div className="flex-1">
                  <h3 className="text-base font-bold text-slate-900 flex items-center gap-2">
                    {lang.nativeLabel}
                    {selected === lang.code && (
                      <ChevronRight className="w-4 h-4" style={{ color: lang.color }} />
                    )}
                  </h3>
                  <p className="text-xs text-slate-500 mt-0.5">
                    {lang.englishLabel}
                  </p>
                </div>
              </div>
            </button>
          ))}
        </div>

        {/* Note */}
        <div className="mt-6 text-center">
          <span className="px-3 py-1 bg-[#0EA5E9]/10 text-[#0EA5E9] text-xs font-semibold rounded-full">
            {t('app.beta', 'Beta')}
          </span>
          <p className="mt-3 text-xs text-slate-400">
            {t('languageSelect.asiaNote', 'Asian languages are in beta. Voice features may vary by device.')}
          </p>
        </div>
      </div>
    </div>
  );
};

export default AsiaLanguageSelectionPage;
